import { useMemo, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { DayColumnHeader, TimeLabel } from '@/components/atoms';
import { AppointmentTile } from '@/components/cards';
import type { AppointmentStatusType } from '@/components/atoms';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { computeOverlapLayout, slotToPercents } from '@/scheduling/lib/overlap-layout';
import { CurrentTimeLine } from '@/components/blocks/current-time-line';
import { GutterClock } from '@/components/blocks/gutter-clock';
import { useScrollToNow } from '@/components/blocks/use-scroll-to-now';
import { SKELETON_BLOCKS, SkeletonEvent } from '@/components/blocks/calendar-skeleton';

/**
 * WeekCalendar — Block
 * 5-day week grid with time gutter, day headers, and appointment tiles.
 * Appointment positioning is calculated from start time and duration.
 *
 * Drag-drop reschedule (PROD-563):
 *   - Each appointment tile is `useDraggable`. Pointer-driven; 6px
 *     activation distance keeps a regular click from being interpreted
 *     as a drag.
 *   - Each 30-minute half-cell is `useDroppable` — the grid is divided
 *     into (days × hours × 2) cells. Dropping on a half-cell maps to a
 *     30-minute target slot; the new start is `cell-start`, the new
 *     end preserves the appointment's original duration.
 *   - On drop, `onAppointmentDrop({ id, newStartISO })` fires. The
 *     parent calls `useRescheduleAppointment` with the new start (the
 *     hook computes the matching end from duration) and handles the
 *     optimistic update + rollback via the existing React Query cache.
 *
 * Recurring + cross-provider scope are out of part 1 — the drop
 * handler stays in the same column as the source tile (we don't
 * remap dayIndex on drop). Cross-day drag and recurring-occurrence-
 * vs-series prompts are PROD-562 follow-up territory.
 *
 * Workflow context: This is the primary view for scheduling.
 */

const HOUR_HEIGHT = 80; // px per hour row
const HALF_HOUR_HEIGHT = HOUR_HEIGHT / 2;
// Default hour gutter when the caller doesn't pass one. PROD-963: previously
// the gutter was hardcoded to 8a–5p, which silently clipped any appointment
// outside business hours (1:55a, 5:45a, 7:00p, etc.). The default still
// applies for stories / standalone use, but real pages MUST pass `hours`
// derived from the practice working-hours + the actual appointment set so
// every tile lands in the visible range (Flutter parity — calendar_by_week
// _widget.dart uses `workingHoursList` for its leading items).
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 17;
const DEFAULT_HOURS = Array.from(
  { length: DEFAULT_END_HOUR - DEFAULT_START_HOUR + 1 },
  (_, i) => i + DEFAULT_START_HOUR,
);

export interface CalendarDay {
  dayOfWeek: string;
  date: number;
  isToday?: boolean;
  /** ISO date (YYYY-MM-DD) for this column — used to build drop-target ids. */
  dateISO?: string;
}

export interface CalendarAppointment {
  id: string;
  patientName: string;
  status: AppointmentStatusType;
  timeRange: string;
  durationMinutes: number;
  startHour: number;
  startMin: number;
  dayIndex: number;
  note?: string;
  hasVideo?: boolean;
  hasChat?: boolean;
  hasNote?: boolean;
  hasFlag?: boolean;
  /** True when this appointment is part of a recurring series. */
  isRecurring?: boolean;
  /** True when this is a Block Availability entry — renders the striped
   *  grey block tile (reason + time) instead of the patient layout. */
  isBlock?: boolean;
  /** Patient importance code/display + location, for the tile meta row
   *  (PROD-990 — importance badge + location icon). All optional. */
  patientImportance?: string;
  patientImportanceDisplay?: string;
  /** Location category (office/home/virtual) selecting the tile glyph. */
  profileLocation?: string;
  /** Human location name for the location-icon tooltip. */
  locationName?: string;
  /** Profile colour (CSS) tinting the location glyph. */
  profileColor?: string;
  /**
   * Full ISO start datetime. Optional — drag-drop reschedule needs it
   * to compute the new start with the dropped-cell offset; views that
   * don't drive the drag flow can omit it.
   */
  startISO?: string;
}

export interface AppointmentDropPayload {
  /** Original appointment id. */
  id: string;
  /** New start as ISO datetime, snapped to the dropped half-cell. */
  newStartISO: string;
  /** Same dayIndex as source — cross-day drag is out of part 1. */
  dayIndex: number;
}

interface WeekCalendarProps {
  days: CalendarDay[];
  appointments: CalendarAppointment[];
  /**
   * Hour gutter to render (e.g. `[5, 6, 7, 8, ..., 20]`). When omitted,
   * defaults to 8a–5p. Real pages should pass a dynamic range derived
   * from the practice working hours and the appointment span (mirrors
   * `hoursForDayList` in calendar-mapping.ts) so early-morning and
   * after-hours bookings don't silently clip — PROD-963.
   */
  hours?: number[];
  onAppointmentClick?: (appointmentId: string) => void;
  onSlotClick?: (dayIndex: number, hour: number) => void;
  /**
   * Fires after a drag completes on a valid drop target. The parent
   * is responsible for the API call + optimistic update. Omit to
   * leave the calendar in click-only mode.
   */
  onAppointmentDrop?: (payload: AppointmentDropPayload) => void;
  /** The week's bookings are still loading: grey blocks in the real grid, nothing else changes. */
  isLoading?: boolean;
}

function computePosition(apt: CalendarAppointment, startHour: number) {
  const top =
    (apt.startHour - startHour) * HOUR_HEIGHT + (apt.startMin / 60) * HOUR_HEIGHT;
  const height = Math.max((apt.durationMinutes / 60) * HOUR_HEIGHT, 24);
  return { top, height };
}

export function WeekCalendar({
  days,
  appointments,
  hours = DEFAULT_HOURS,
  onAppointmentClick,
  onSlotClick,
  onAppointmentDrop,
  isLoading = false,
}: WeekCalendarProps) {
  // First hour in the visible gutter — anchors absolute positioning so
  // a 5:45a appointment renders at the top of the 5a row when the gutter
  // includes early hours (PROD-963 fix). Falls back to DEFAULT_START_HOUR
  // for the empty-hours edge case.
  // A week change animates (Antonin, 12 Oct, both apps): the grid is keyed by its first day and slides in from the side
  // the user moved towards (index.css .cal-swap-next / -prev).
  const weekKey = days[0]?.dateISO ?? `${days[0]?.dayOfWeek}-${days[0]?.date}`;
  const prev = useRef<{ key: string; iso?: string; date?: number }>({ key: weekKey, iso: days[0]?.dateISO, date: days[0]?.date });
  const swap = useRef<'' | 'cal-swap-next' | 'cal-swap-prev'>('');
  if (prev.current.key !== weekKey) {
    // Forward or back: by ISO date when the days carry one, else by day of month (a jump of more than 20 days the
    // other way is a month boundary, e.g. 26 -> 2 is forward).
    const iso = days[0]?.dateISO;
    let forward: boolean;
    if (iso && prev.current.iso) forward = iso > prev.current.iso;
    else {
      const d = (days[0]?.date ?? 0) - (prev.current.date ?? 0);
      forward = d > 0 ? d <= 20 : d < -20;
    }
    swap.current = forward ? 'cal-swap-next' : 'cal-swap-prev';
    prev.current = { key: weekKey, iso, date: days[0]?.date };
  }
  const startHour = hours[0] ?? DEFAULT_START_HOUR;
  const visibleHours = hours.length > 0 ? hours : DEFAULT_HOURS;
  // 6px activation distance — large enough that a normal click on the
  // tile doesn't get hijacked as a drag, small enough that the user
  // doesn't have to yank to start.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const apptById = useMemo(() => {
    const m = new Map<string, CalendarAppointment>();
    for (const a of appointments) m.set(a.id, a);
    return m;
  }, [appointments]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onAppointmentDrop) return;
    const apptId = String(event.active.id);
    const overId = event.over?.id;
    if (!overId) return;

    // Drop-target id shape: `cell-<dayIndex>-<hour>-<halfIndex>`
    // halfIndex: 0 = top half (xx:00), 1 = bottom half (xx:30)
    const match = String(overId).match(/^cell-(\d+)-(\d+)-(\d+)$/);
    if (!match) return;
    const dayIndex = Number(match[1]);
    const targetHour = Number(match[2]);
    const targetHalf = Number(match[3]);

    const apt = apptById.get(apptId);
    if (!apt) return;

    // Part 1: same-column only. Cross-day drag is a follow-up.
    if (apt.dayIndex !== dayIndex) return;

    // Cancel the drop if it lands on the source cell — no-op.
    const sourceHalf = apt.startMin < 30 ? 0 : 1;
    if (apt.startHour === targetHour && sourceHalf === targetHalf) return;

    // Compute the new start datetime. Prefer startISO when present
    // (lets us preserve the date) — otherwise fall back to today.
    // Format via the no-zone formatter (matches what the parent will
    // send on the wire — keeps the equality check below consistent).
    const baseDate = apt.startISO ? new Date(apt.startISO) : new Date();
    baseDate.setHours(targetHour, targetHalf === 0 ? 0 : 30, 0, 0);
    const newStartWire = formatLocalDatetimeForWire(baseDate);

    // Belt-and-braces: if the formatted new start equals the source
    // (when normalized through the same formatter), no-op. Catches
    // the case where startISO already carries the target time so the
    // (hour, half) check above could miss a sub-half-hour shift.
    if (apt.startISO && formatLocalDatetimeForWire(apt.startISO) === newStartWire) {
      return;
    }

    onAppointmentDrop({
      id: apptId,
      newStartISO: newStartWire,
      dayIndex,
    });
  };

  // Open on now (shared with Doctor and Room, use-scroll-to-now.ts).
  const weekScrollRef = useRef<HTMLDivElement>(null);
  useScrollToNow(weekScrollRef, {
    startHour,
    endHour: (visibleHours[visibleHours.length - 1] ?? startHour) + 1,
    hourHeight: HOUR_HEIGHT,
    key: weekKey,
  });

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex flex-col flex-1 min-h-0">
        <div ref={weekScrollRef} key={weekKey} data-cal-scroll className={`scrollbar-hide ${swap.current}`}>
         <div data-cal-sheet>
          {/* Day headers — pinned to the top, the clock corner to the left too (showcase-bridge `[data-cal-*]`). */}
          <div data-cal-head className="flex border-b border-border-default">
            {/* PROD-1797 — was the literal `GMT+04`; see gutter-clock.tsx. */}
            <GutterClock data-cal-gutter className="w-16 flex-shrink-0 px-2 py-2 border-r border-border-subtle" />
            {days.map((d, i) => (
              <div key={i} className="flex-1 border-r border-border-subtle last:border-r-0">
                <DayColumnHeader dayOfWeek={d.dayOfWeek} date={d.date} isToday={d.isToday} />
              </div>
            ))}
          </div>

          {/* Time grid — each hour-cell splits into two droppable half-cells. */}
          <div className="relative">
            {visibleHours.map((hour) => (
              <div key={hour} className="flex h-[80px] border-b border-border-subtle">
                <div data-cal-gutter className="w-16 flex-shrink-0 px-2 pt-1 border-r border-border-subtle">
                  <TimeLabel hour={hour} />
                </div>
                {days.map((d, dayIdx) => (
                  <DayHourCell
                    key={dayIdx}
                    isToday={d.isToday}
                    dayIndex={dayIdx}
                    hour={hour}
                    onSlotClick={onSlotClick}
                  />
                ))}
              </div>
            ))}

            {/* Appointment tiles overlay — draggable. */}
            <div className="absolute inset-0 flex pointer-events-none">
              <div className="w-16 flex-shrink-0" />
              {days.map((day, dayIdx) => {
                // Side-by-side layout for overlapping appointments in this
                // day column (PROD-982). Wrap each appt with its minute-range
                // so the layout Map keys back to it.
                const items = appointments
                  .filter((a) => a.dayIndex === dayIdx)
                  .map((apt) => {
                    const start = apt.startHour * 60 + apt.startMin;
                    return { apt, startMin: start, endMin: start + apt.durationMinutes };
                  });
                const layout = computeOverlapLayout(items);
                return (
                  <div key={dayIdx} className="flex-1 relative">
                    {isLoading && appointments.length === 0 &&
                      SKELETON_BLOCKS.filter((b) => b.col === dayIdx && b.at + b.len <= visibleHours.length).map((b, i) => (
                        <SkeletonEvent key={`sk-${i}`} top={b.at * HOUR_HEIGHT} height={b.len * HOUR_HEIGHT} />
                      ))}
                    {items.map((it) => {
                      const { apt } = it;
                      const { top, height } = computePosition(apt, startHour);
                      const slot = layout.get(it);
                      const { leftPct, widthPct } =
                        slot && slot.columnCount > 1
                          ? slotToPercents(slot)
                        : { leftPct: undefined, widthPct: undefined };
                      return (
                        <DraggableTile
                          key={apt.id}
                          apt={apt}
                          top={top}
                          height={height}
                          leftPct={leftPct}
                          widthPct={widthPct}
                          onClick={() => onAppointmentClick?.(apt.id)}
                          draggable={!!onAppointmentDrop}
                        />
                      );
                    })}
                    {/* Current-time "now" line (PROD-981) — today's column only. */}
                    {day.isToday ? (
                      <CurrentTimeLine
                        startHour={startHour}
                        endHour={(visibleHours[visibleHours.length - 1] ?? startHour) + 1}
                        hourHeight={HOUR_HEIGHT}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
         </div>
        </div>
      </div>
    </DndContext>
  );
}

interface DayHourCellProps {
  dayIndex: number;
  /** Today's column is tinted blue-100, the active state (practitioner calendar). */
  isToday?: boolean;
  hour: number;
  onSlotClick?: (dayIndex: number, hour: number) => void;
}

/**
 * One hour-cell split into two stacked half-cells. Each half is a
 * separate droppable so the snap target lands on 00 or 30 minutes.
 */
function DayHourCell({ dayIndex, isToday, hour, onSlotClick }: DayHourCellProps) {
  return (
    <div
      className={`flex-1 border-r border-border-subtle last:border-r-0 relative cursor-pointer flex flex-col${isToday ? ' bg-[color-mix(in_srgb,var(--color-brand-primary)_3%,white)]' : ''}`}
      onClick={() => onSlotClick?.(dayIndex, hour)}
    >
      <HalfHourCell dayIndex={dayIndex} hour={hour} half={0} />
      <HalfHourCell dayIndex={dayIndex} hour={hour} half={1} isBottom />
    </div>
  );
}

function HalfHourCell({
  dayIndex,
  hour,
  half,
  isBottom,
}: {
  dayIndex: number;
  hour: number;
  half: 0 | 1;
  isBottom?: boolean;
}) {
  const id = `cell-${dayIndex}-${hour}-${half}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={[
        'flex-1 hover:bg-blue-100/20',
        isBottom ? 'border-t border-border-subtle border-dashed' : '',
        isOver ? 'bg-blue-100/60 outline outline-2 -outline-offset-2 outline-brand-primary' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ minHeight: HALF_HOUR_HEIGHT }}
    />
  );
}

interface DraggableTileProps {
  apt: CalendarAppointment;
  top: number;
  height: number;
  /** Overlap geometry (PROD-982) — forwarded to AppointmentTile. */
  leftPct?: number;
  widthPct?: number;
  onClick?: () => void;
  /** When false, the tile renders without DnD listeners — click-only mode. */
  draggable: boolean;
}

function DraggableTile({ apt, top, height, leftPct, widthPct, onClick, draggable }: DraggableTileProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: apt.id,
    disabled: !draggable,
  });

  const dragStyle = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      className="pointer-events-auto"
      style={{
        ...dragStyle,
        opacity: isDragging ? 0.5 : 1,
        // Tile uses absolute positioning internally — keep it.
      }}
      {...(draggable ? listeners : {})}
      {...(draggable ? attributes : {})}
    >
      <AppointmentTile
        patientName={apt.patientName}
        status={apt.status}
        timeRange={apt.timeRange}
        durationMinutes={apt.durationMinutes}
        note={apt.note}
        hasVideo={apt.hasVideo}
        hasChat={apt.hasChat}
        hasNote={apt.hasNote}
        hasFlag={apt.hasFlag}
        isRecurring={apt.isRecurring}
        isBlock={apt.isBlock}
        patientImportance={apt.patientImportance}
        patientImportanceDisplay={apt.patientImportanceDisplay}
        profileLocation={apt.profileLocation}
        locationName={apt.locationName}
        profileColor={apt.profileColor}
        height={height}
        top={top}
        leftPct={leftPct}
        widthPct={widthPct}
        onClick={onClick}
        isPast={!!apt.startISO && new Date(apt.startISO).getTime() + apt.durationMinutes * 60_000 < Date.now()}
      />
    </div>
  );
}
